import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import { fleetService } from '../../services/fleetService';
import UserStatusBadge from '../../components/users/UserStatusBadge';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import {
    Search,
    UserPlus,
    Eye,
    Edit,
    Trash,
    Filter,
    AlertTriangle
} from 'lucide-react';

const UsersPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [fleets, setFleets] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, userId: null });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10
    });
    const [filters, setFilters] = useState({
        status: '',
        user_type: '',
        fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
        search: ''
    });

    useEffect(() => {
        fetchUsers();
        if (user.user_type === 'admin') {
            fetchFleets();
        }
    }, [pagination.currentPage, filters]);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const { data, meta } = await userService.getUsers({
                page: pagination.currentPage,
                limit: pagination.perPage,
                ...filters
            });

            setUsers(data);
            setTotalUsers(meta.total);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleUserTypeChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, user_type: value }));
    };

    const handleFleetChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, fleet_id: value }));
    };

    const handleFilterReset = () => {
        setFilters({
            status: '',
            user_type: '',
            fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
            search: ''
        });
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleViewUser = (userId) => {
        navigate(`/users/${userId}`);
    };

    const handleEditUser = (userId) => {
        navigate(`/users/${userId}/edit`);
    };

    const confirmDelete = (userId) => {
        setDeleteConfirmation({ show: true, userId });
    };

    const handleDeleteUser = async () => {
        if (!deleteConfirmation.userId) return;

        try {
            await userService.deleteUser(deleteConfirmation.userId);
            setDeleteConfirmation({ show: false, userId: null });
            fetchUsers(); // 重新加載用戶列表
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const columns = [
        {
            header: '用戶',
            accessor: 'username',
            cell: (row) => (
                <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 overflow-hidden">
                        {row.avatar_url && (
                            <img
                                src={row.avatar_url}
                                alt={row.username}
                                className="w-full h-full object-cover"
                            />
                        )}
                    </div>
                    <div>
                        <p className="font-medium">{row.username}</p>
                        <p className="text-xs text-gray-500">{row.email}</p>
                    </div>
                </div>
            )
        },
        {
            header: '用戶類型',
            accessor: 'user_type',
            cell: (row) => {
                const userTypeMap = {
                    'admin': '管理員',
                    'dispatcher': '調度員',
                    'fleet_manager': '車隊經理',
                    'support': '技術支援'
                };
                return <span>{userTypeMap[row.user_type] || row.user_type}</span>;
            }
        },
        {
            header: '所屬車隊',
            accessor: 'fleet_name',
            cell: (row) => (
                <span>{row.fleet_name || '無'}</span>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <UserStatusBadge status={row.status} />
            )
        },
        {
            header: '上次登入',
            accessor: 'last_login',
            cell: (row) => (
                <span className="text-sm text-gray-600">
                    {row.last_login
                        ? new Date(row.last_login).toLocaleString('zh-TW', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                        })
                        : '從未登入'}
                </span>
            )
        },
        {
            header: '操作',
            accessor: 'actions',
            cell: (row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleViewUser(row.user_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="查看詳情"
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleEditUser(row.user_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="編輯用戶"
                    >
                        <Edit className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => confirmDelete(row.user_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title="刪除用戶"
                    >
                        <Trash className="w-5 h-5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">用戶管理</h1>
                <button
                    onClick={() => navigate('/users/new')}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <UserPlus className="w-5 h-5 mr-2" />
                    新增用戶
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex-1 max-w-md relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                placeholder="搜尋用戶名稱或Email..."
                                value={filters.search}
                                onChange={handleSearch}
                            />
                        </div>

                        {user.user_type === 'admin' && (
                            <select
                                className="border border-gray-300 rounded-md px-3 py-2"
                                value={filters.fleet_id}
                                onChange={handleFleetChange}
                            >
                                <option value="">所有車隊</option>
                                {fleets.map(fleet => (
                                    <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                        {fleet.fleet_name}
                                    </option>
                                ))}
                            </select>
                        )}

                        <select
                            className="border border-gray-300 rounded-md px-3 py-2"
                            value={filters.user_type}
                            onChange={handleUserTypeChange}
                        >
                            <option value="">所有用戶類型</option>
                            <option value="admin">管理員</option>
                            <option value="dispatcher">調度員</option>
                            <option value="fleet_manager">車隊經理</option>
                            <option value="support">技術支援</option>
                        </select>

                        <select
                            className="border border-gray-300 rounded-md px-3 py-2"
                            value={filters.status}
                            onChange={handleStatusChange}
                        >
                            <option value="">所有狀態</option>
                            <option value="active">啟用</option>
                            <option value="inactive">停用</option>
                            <option value="pending">待審核</option>
                        </select>

                        {(filters.status !== '' || filters.search !== '' || filters.user_type !== '' || filters.fleet_id !== '') && (
                            <button
                                onClick={handleFilterReset}
                                className="text-blue-500 hover:underline text-sm"
                            >
                                重置篩選
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <DataTable
                            columns={columns}
                            data={users}
                            emptyMessage="無符合條件的用戶"
                        />
                        <div className="p-4 border-t border-gray-200">
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalItems={totalUsers}
                                perPage={pagination.perPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* 刪除確認對話框 */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0 mt-0.5">
                                <AlertTriangle className="h-6 w-6 text-red-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">確認刪除</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    您確定要刪除此用戶嗎？此操作無法恢復，並會移除該用戶的所有權限和存取。
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, userId: null })}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                取消
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                            >
                                確認刪除
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;